// ./src/components/features/Memberships/MembershipTypesList/MembershipTypesList.tsx
//
// MembershipTypes list with a different presentation per breakpoint, switched
// live on window resize (useAppEnvironment is backed by useMediaQuery):
//
// - Desktop (>= md): a compact TABLE — swatch · name · scope · pricing · created
//   · row actions.
// - Tablet / mobile (< md): a CARD list (swatch + name + a muted
//   "<scope> · <pricing>" subtitle + created date).
//
// Interaction (best-practice, tap-safe):
// - Rows/cards are NEVER click-to-edit. Editing happens only via the explicit
//   Edit button, so an accidental tap can't open the form.
// - Desktop + tablet: Edit/Delete buttons are always visible.
// - Phones (< sm): swipe-to-reveal Edit/Delete; tapping the card does nothing.
//
// Data resolves from <MembershipTypesProvider> when props are omitted.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { MembershipType } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalMembershipTypesContext } from "@/contexts/MembershipTypesContext";
import {
  AppIcon,
  ColorSwatch,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Types
// ============================================

export interface MembershipTypesListProps {
  /** Array of membership type records. Optional when inside provider. */
  items?: MembershipType[];

  /** Loading state for initial load. Optional when inside provider. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when the Edit action is invoked (button or swipe). */
  onEditType?: (item: MembershipType) => void;

  /** Callback when the Delete action is invoked (button or swipe). */
  onDeleteType?: (item: MembershipType) => void;

  /** Predicate for whether an item is being removed. Optional — reads from context. */
  isRemoving?: (id?: string) => boolean;

  /** ID of recently edited/added item to highlight */
  highlightedId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddType?: () => void;

  /** Multi-row selection state. When provided (and enabled), rows are selectable. */
  selection?: RowSelection;

  /**
   * Whether selection controls are shown. On desktop/tablet this means checkboxes;
   * on phones the row is tap-to-toggle only while `selection.selectionMode` is on.
   */
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Friendly labels
// ============================================

const useLabelHelpers = () => {
  const { t } = useTranslation(["membershipTypes"]);

  const scopeLabel = useCallback(
    (scope: string | null | undefined): string => {
      switch (scope) {
        case "individual": {
          return t("membershipTypes:scope.individual", {
            defaultValue: "Individual",
          });
        }
        case "household": {
          return t("membershipTypes:scope.household", {
            defaultValue: "Household",
          });
        }
        case "company": {
          return t("membershipTypes:scope.company", { defaultValue: "Company" });
        }
        default: {
          return "—";
        }
      }
    },
    [t]
  );

  const pricingLabel = useCallback(
    (pricing: string | null | undefined): string => {
      switch (pricing) {
        case "one_time": {
          return t("membershipTypes:pricing.one_time", {
            defaultValue: "One-time",
          });
        }
        case "subscription": {
          return t("membershipTypes:pricing.subscription", {
            defaultValue: "Subscription",
          });
        }
        default: {
          return "—";
        }
      }
    },
    [t]
  );

  return { scopeLabel, pricingLabel };
};

// ============================================
// Shared bits
// ============================================

// Compact badges shown next to a type's name: its number prefix (when set) and
// an "External" marker (memberships imported from an external system).
const TypeBadges: React.FC<{ item: MembershipType }> = ({ item }) => {
  const { t } = useTranslation(["membershipTypes", "common"]);

  const disabled = item.status === STATUSES.DISABLED;

  if (!item.numberPrefix && !item.isExternal && !disabled) {
    return null;
  }

  return (
    <>
      {item.numberPrefix && (
        <Badge size="xs" variant="light" color="gray" styles={{ label: { fontFamily: "monospace" } }}>
          {item.numberPrefix}
        </Badge>
      )}
      {item.isExternal && (
        <Badge size="xs" variant="light" color="grape">
          {t("membershipTypes:badges.external", { defaultValue: "External" })}
        </Badge>
      )}
      {disabled && (
        <Badge size="xs" variant="light" color="gray">
          {t("common:status.disabled", { defaultValue: "Disabled" })}
        </Badge>
      )}
    </>
  );
};

// Always-visible row actions (desktop table + tablet cards). Explicit buttons
// are the ONLY way to edit/delete, so there's nothing to discover on hover.
const RowActions: React.FC<{
  item: MembershipType;
  onEdit?: (item: MembershipType) => void;
  onDelete?: (item: MembershipType) => void;
  isDeleting: boolean;
}> = ({ item, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  // Per-record rights (from the API) gate the buttons on top of page-level rights.
  const showEdit = !!onEdit && recordHasRight(item, "edit");
  const showDelete = !!onDelete && recordHasRight(item, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(item)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(item)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Shared row shape
// ============================================

interface RowProps {
  item: MembershipType;
  onEdit?: (item: MembershipType) => void;
  onDelete?: (item: MembershipType) => void;
  highlighted: boolean;
  isDeleting: boolean;
  /** i18n language for the localized timestamp. */
  language: string;
  scopeLabel: (scope: string | null | undefined) => string;
  pricingLabel: (pricing: string | null | undefined) => string;
  /** Whether the desktop table has a leading selection column (list-level). */
  selectionColumn: boolean;
  /**
   * Whether THIS row can be selected — only if there's a bulk action the caller
   * can run on it (change → 'edit' right, or delete → 'remove' right). A row with
   * neither shows no checkbox and can't be toggled.
   */
  selectable: boolean;
  /** Whether this row is currently selected. */
  selected: boolean;
  /** Phones: selection mode is active (tap the card to toggle). */
  selectionMode: boolean;
  /** Toggle this row's selection (stable). */
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop: Table row (not clickable)
// ============================================

const MembershipTypeTableRow: React.FC<RowProps> = ({
  item,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  scopeLabel,
  pricingLabel,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : item.status === STATUSES.DISABLED ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => item.id && onToggleSelect?.(item.id)}
              disabled={isDeleting}
              aria-label={item.name ?? undefined}
            />
          )}
        </Table.Td>
      )}
      <Table.Td style={{ width: 52 }}>
        <ColorSwatch color={item.color} size={26} />
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text size="md" fw={500} lineClamp={1}>
            {item.name || "—"}
          </Text>
          <TypeBadges item={item} />
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <Text size="sm" c="dimmed">
          {scopeLabel(item.memberScope)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <Text size="sm" c="dimmed">
          {pricingLabel(item.pricingModel)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 210 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(item.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 96 }}>
        <RowActions
          item={item}
          onEdit={onEdit}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </Table.Td>
    </Table.Tr>
  );
};

// A skeleton row that IS a real Table.Tr — identical column widths, spacing and
// element sizes to MembershipTypeTableRow, so loading matches the loaded table.
const MembershipTypeTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({
  selectionColumn,
}) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td style={{ width: 52 }}>
      <Skeleton height={26} width={26} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="60%" maw={220} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={16} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Skeleton height={16} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 210 }}>
      <Skeleton height={16} width={150} />
    </Table.Td>
    <Table.Td style={{ width: 96 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Mobile / tablet: Card (not clickable)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const MembershipTypeCardComponent: React.FC<CardProps> = ({
  item,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  scopeLabel,
  pricingLabel,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => item.id && onToggleSelect?.(item.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(item, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(item),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(item, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(item),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, item, isDeleting, t]);

  const subtitle = `${scopeLabel(item.memberScope)} · ${pricingLabel(
    item.pricingModel
  )}`;

  // showActions=false on phones (swipe reveals them); true on tablet.
  // showCheckbox renders a leading selection checkbox.
  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : item.status === STATUSES.DISABLED ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={item.name ?? undefined}
          />
        )}
        <ColorSwatch color={item.color} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={500} lineClamp={1}>
              {item.name || "—"}
            </Text>
            <TypeBadges item={item} />
          </Group>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {subtitle}
          </Text>
          <Text size="xs" c="dimmed">
            {formatSmartDateTime(item.createdAt, language)}
          </Text>
        </Box>
        {showActions && (
          <RowActions
            item={item}
            onEdit={onEdit}
            onDelete={onDelete}
            isDeleting={isDeleting}
          />
        )}
      </Group>
    </Paper>
  );

  // Selection mode (entered on phones): a selectable card is a tap-to-toggle
  // target with a leading checkbox; a non-selectable one (no edit/remove right)
  // shows neither and can't be toggled. Tablet keeps its checkbox + action
  // buttons (a stray card tap shouldn't toggle there).
  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  // Not in selection mode: phones NEVER show a checkbox (selection is a mode on
  // phones) — they swipe to reveal actions, or show a plain card when there are
  // none. Tablet shows action buttons + a checkbox for selectable rows.
  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const MembershipTypeCard = React.memo(MembershipTypeCardComponent);

// A skeleton card matching MembershipTypeCardComponent's cardBody: same padding,
// 32px swatch, three-line name+subtitle+date block, and tablet-only actions.
const MembershipTypeCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton height={32} width={32} radius="sm" />
      <CardSkeletonLines count={3} sizes={['md', 'xs', 'xs']} gap={0} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main Component
// ============================================

const MembershipTypesListComponent: React.FC<MembershipTypesListProps> = ({
  items: itemsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditType,
  onDeleteType,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddType,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["membershipTypes", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditType, onDeleteType);

  const { scopeLabel, pricingLabel } = useLabelHelpers();

  // ── Context vs Props resolution ──
  const ctx = useOptionalMembershipTypesContext();

  const items = itemsProp ?? ctx?.membershipTypes ?? [];
  const loading = loadingProp ?? ctx?.loading ?? false;
  const loadingMore = loadingMoreProp ?? ctx?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? ctx?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? ctx?.loadMore;
  const isRemoving = isRemovingProp ?? ctx?.isRemoving ?? (() => false);

  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  const { hasAnimated } = useEntranceAnimation({ itemCount: items.length });

  // ── Wrapped handlers (close any open swipe before acting) ──
  const handleEdit = useCallback(
    (item: MembershipType) => {
      closeAllCards();
      onEditType?.(item);
    },
    [onEditType, closeAllCards]
  );

  const handleDelete = useCallback(
    (item: MembershipType) => {
      closeAllCards();
      onDeleteType?.(item);
    },
    [onDeleteType, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = ctx?.isLoaded ?? itemsProp !== undefined;
  const showSkeleton = !isLoaded;

  // ── Empty ──
  if (!showSkeleton && items.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("membershipTypes:emptyState.noMatch", { defaultValue: "No membership types match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("membershipTypes:emptyState.title", {
            defaultValue: "No membership types yet",
          })}
          description={t("membershipTypes:emptyState.description", {
            defaultValue: "Create a membership type to get started.",
          })}
          iconName="Award"
          actionLabel={t("membershipTypes:emptyState.actionLabel", {
            defaultValue: "Add membership type",
          })}
          onAction={onAddType}
          size="md"
        />
      </Paper>
    );
  }

  // A row is selectable only if there's a bulk action the caller can actually run
  // on it: change (row edit callback present + 'edit' right) or delete (row delete
  // callback present + 'remove' right). Selection exists for bulk update/removal,
  // so a record that supports neither is never selectable.
  const rowIsSelectable = (item: MembershipType): boolean =>
    isSelectable &&
    ((!!onEditType && recordHasRight(item, "edit")) ||
      (!!onDeleteType && recordHasRight(item, "remove")));

  const rowProps = (item: MembershipType): RowProps => ({
    item,
    onEdit: onEditType ? handleEdit : undefined,
    onDelete: onDeleteType ? handleDelete : undefined,
    highlighted: item.id === highlightedId,
    isDeleting: isRemoving(item.id),
    language,
    scopeLabel,
    pricingLabel,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(item),
    selected: selection?.isSelected(item.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  // The "select all" checkbox operates only on the selectable loaded rows.
  const selectableVisibleIds = items
    .filter((i) => rowIsSelectable(i))
    .map((i) => i.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton
      hasMore={hasMore}
      loading={loading}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
    />
  );

  // ── Desktop: table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", {
                        defaultValue: "Select all",
                      })}
                    />
                  </Table.Th>
                )}
                <Table.Th style={{ width: 52 }} />
                <Table.Th>
                  {t("membershipTypes:list.columns.name", {
                    defaultValue: "Name",
                  })}
                </Table.Th>
                <Table.Th style={{ width: 140 }}>
                  {t("membershipTypes:list.columns.scope", {
                    defaultValue: "Scope",
                  })}
                </Table.Th>
                <Table.Th style={{ width: 140 }}>
                  {t("membershipTypes:list.columns.pricing", {
                    defaultValue: "Pricing",
                  })}
                </Table.Th>
                <Table.Th style={{ width: 210 }}>
                  {t("membershipTypes:list.columns.createdAt", {
                    defaultValue: "Created",
                  })}
                </Table.Th>
                <Table.Th style={{ width: 96 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => (
                    <MembershipTypeTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : items.map((item) => (
                    <MembershipTypeTableRow key={item.id} {...rowProps(item)} />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile: cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <MembershipTypeCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <AnimatePresence mode="sync">
          {items.map((item) => (
            <motion.div
              key={item.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <MembershipTypeCard
                {...rowProps(item)}
                forceClose={shouldForceClose(item.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(item.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(item.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const MembershipTypesList = React.memo(MembershipTypesListComponent);
